import { useTranslation } from 'react-i18next';
import { DynamicCombobox } from '@shared/components/ui/DynamicCombobox';
import { useTenantChildDynamicCombobox } from '@shared/hooks/useTenantChildDynamicCombobox';
import { Alert, AlertDescription } from '@shared/components/ui/shadcn/alert';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCircleInfo } from '@fortawesome/pro-light-svg-icons';
import { TenantChildSelectorSkeleton } from '../skeletons/TenantChildSelectorSkeleton';

interface TenantChildSelectorProps {
  parentTeamId: number | undefined;
  comboboxEnabled: boolean;
  selectedTenantChildId: number | null;
  onSelectTenantChild: (value: string) => void;
  isLocked: boolean;
}

/**
 * Selector de cliente en creación de pedido (3PL): búsqueda + scroll infinito.
 */
export const TenantChildSelector = ({
  parentTeamId,
  comboboxEnabled,
  selectedTenantChildId,
  onSelectTenantChild,
  isLocked,
}: TenantChildSelectorProps) => {
  const { t } = useTranslation('createEditOrdersModule');

  const {
    searchFn,
    onLoadMore,
    hasMore,
    isLoadingBrowse,
    isFetchedBrowse,
    browseTotal,
    resolveSelectedOption,
    fetchError,
  } = useTenantChildDynamicCombobox({
    parentTeamId,
    enabled: comboboxEnabled,
  });

  const hasClients = (browseTotal ?? 1) > 0;
  const listEmptyAfterFetch = Boolean(
    comboboxEnabled && isFetchedBrowse && browseTotal !== undefined && browseTotal === 0,
  );

  const showInitialSkeleton = comboboxEnabled && isLoadingBrowse && !selectedTenantChildId;

  const displayError = fetchError ?? null;

  if (showInitialSkeleton) {
    return <TenantChildSelectorSkeleton />;
  }

  return (
    <div className="border border-card-border rounded-lg p-4 mb-6 shadow-sm">
      <div>
        <h2 className="text-lg font-semibold text-title">{t('tenantChildSelector.title')}</h2>
        <p className="text-sm text-complementary-text mt-1">{t('tenantChildSelector.description')}</p>
      </div>

      <div className="mt-4">
        <DynamicCombobox
          value={selectedTenantChildId ? String(selectedTenantChildId) : ''}
          setValue={onSelectTenantChild}
          placeholder={
            comboboxEnabled && hasClients ? t('tenantChildSelector.placeholder') : t('tenantChildSelector.empty')
          }
          searchFn={searchFn}
          resolveSelectedOption={resolveSelectedOption}
          onLoadMore={onLoadMore}
          hasMore={hasMore}
          className="w-full"
          disabled={!comboboxEnabled || listEmptyAfterFetch}
          dataTestId="tenant-child-combobox"
        />
      </div>

      <div className="mt-4 space-y-2">
        {displayError && (
          <Alert className="border-error text-error">
            <AlertDescription>{displayError}</AlertDescription>
          </Alert>
        )}
        {listEmptyAfterFetch && !displayError && (
          <Alert className="border-warning text-warning">
            <AlertDescription>{t('tenantChildSelector.emptyDescription')}</AlertDescription>
          </Alert>
        )}
        {isLocked && comboboxEnabled && hasClients && (
          <div className="flex items-center gap-3 rounded-lg border border-card-border bg-card-bg px-4 py-4 text-sm text-title">
            <FontAwesomeIcon icon={faCircleInfo} className="w-4 h-4 text-icon" />
            <span>{t('tenantChildSelector.lockedMessage')}</span>
          </div>
        )}
      </div>
    </div>
  );
};
